/**
 * Müşteriler page over GET /customers: who wrote and who bought. A source table (conversations,
 * replies, conversions, orders, revenue per source), then every customer with the address they
 * write from, the vehicles they asked about and, on tap, their timeline of messages, decisions
 * and orders. Read only; nothing here sends or decides.
 */

import { useEffect, useId, useState } from 'react';
import { type Customer, type CustomerEvent, customerSourceLabel, ordersText } from '../api/customers.js';
import { centsToMicro } from '../api/shop.js';
import { type CustomersState, useCustomers } from '../app/customers.js';
import { formatClock, formatDayMonth, formatPct, formatUsd } from '../lib/format.js';
import { EmptyState } from '../screens/common.js';
import { SectionHeader } from './primitives.js';

function useCustomersView(): { state: CustomersState; reload: () => void } {
  const { store, state } = useCustomers();
  useEffect(() => {
    void store.ensureLoaded();
  }, [store]);
  return { state, reload: () => void store.reload() };
}

function CustomersStatus({ state, reload }: { state: CustomersState; reload: () => void }) {
  if (state.error !== null && state.view === null) {
    return (
      <div className="shop-status" role="status">
        Müşteri verisi yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={reload}>
          Tekrar dene
        </button>
      </div>
    );
  }
  return (
    <div className="shop-status" role="status">
      Yükleniyor…
    </div>
  );
}

const EVENT_CLASS: Readonly<Record<CustomerEvent['kind'], string>> = {
  in: 'customers-event--in',
  out: 'customers-event--out',
  order: 'customers-event--order',
  approved: 'customers-event--decision',
  edited: 'customers-event--decision',
  rejected: 'customers-event--rejected',
  own_reply: 'customers-event--out',
};

function TimelineItem({ event }: { event: CustomerEvent }) {
  return (
    <li className={`customers-event ${EVENT_CLASS[event.kind]}`}>
      <span className="customers-event__when mono">
        {formatDayMonth(event.at)} {formatClock(event.at)}
      </span>
      <span className="customers-event__body">
        <span className="customers-event__title">
          {event.title}
          {event.amountCents !== null ? (
            <span className="mono customers-event__amount"> {formatUsd(centsToMicro(event.amountCents))}</span>
          ) : null}
        </span>
        {event.text ? <span className="customers-event__text">{event.text}</span> : null}
      </span>
    </li>
  );
}

function matches(customer: Customer, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase('tr');
  if (needle === '') return true;
  const hay = [
    customer.name,
    ...customer.vehicles,
    ...customer.sources.map(customerSourceLabel),
    customer.countryCode ?? '',
  ]
    .join(' ')
    .toLocaleLowerCase('tr');
  return hay.includes(needle);
}

function CustomerRow({ customer, open, onToggle }: { customer: Customer; open: boolean; onToggle: () => void }) {
  const timelineId = useId();
  const orders = ordersText(customer.orders);
  return (
    <li className={customer.converted ? 'customers-row customers-row--converted' : 'customers-row'}>
      <button
        type="button"
        className="customers-row__main"
        aria-expanded={open}
        aria-controls={open ? timelineId : undefined}
        onClick={onToggle}
      >
        <span className="customers-row__head">
          <span className="customers-row__name">{customer.name}</span>
          {customer.countryCode ? <span className="country mono">{customer.countryCode}</span> : null}
          <span className="customers-row__last mono">{formatDayMonth(customer.lastSeenAt)}</span>
        </span>
        <span className="customers-row__meta">
          {customer.sources.map(customerSourceLabel).join(', ')}
          {customer.vehicles.length > 0 ? ` · ${customer.vehicles.join(', ')}` : ''}
          {customer.conversations > 0 ? ` · ${customer.conversations} konuşma` : ''}
          {customer.conversations > 0 && !customer.replied ? ' · cevap bekliyor' : ''}
          {orders ? ` · ${orders}, ${formatUsd(centsToMicro(customer.revenueCents), 0)}` : ''}
          {customer.converted ? <span className="customers-row__converted"> · satışa döndü</span> : null}
        </span>
      </button>
      {open ? (
        <ol id={timelineId} className="customers-timeline" aria-label={`${customer.name} zaman çizgisi`}>
          {customer.timeline.map((event) => (
            <TimelineItem key={event.id} event={event} />
          ))}
        </ol>
      ) : null}
    </li>
  );
}

export function CustomersPage() {
  const { state, reload } = useCustomersView();
  const [open, setOpen] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  if (state.view === null) return <CustomersStatus state={state} reload={reload} />;
  const view = state.view;
  if (view.totals.customers === 0) {
    return (
      <EmptyState
        title="Henüz müşteri kaydı yok"
        text="E-posta, form ve Etsy konuşmaları ile siparişler okundukça burada birikir."
      />
    );
  }
  const filtered = view.customers.filter((customer) => matches(customer, query));
  const totals = view.totals;
  return (
    <>
      <section aria-labelledby="musteri-ozet" className="section section--tight">
        <SectionHeader id="musteri-ozet" title="Özet" />
        <p className="customers-summary">
          <span className="mono">{totals.customers}</span> müşteri ·{' '}
          <span className="mono">{totals.conversations}</span> konuşma, <span className="mono">{totals.replied}</span>{' '}
          cevaplandı · <span className="mono">{totals.converted}</span> satışa döndü ·{' '}
          <span className="mono">{totals.orders}</span> sipariş,{' '}
          <span className="mono">{formatUsd(centsToMicro(totals.revenueCents), 0)}</span>
        </p>
      </section>

      <section aria-labelledby="musteri-kaynak" className="section">
        <SectionHeader id="musteri-kaynak" title="Kaynağa göre" count={view.sources.length} />
        <div className="customers-sources__wrap">
          <table className="customers-sources" aria-label="Kaynağa göre dönüşüm">
            <thead>
              <tr>
                <th scope="col">Kaynak</th>
                <th scope="col">Konuşma</th>
                <th scope="col">Cevap</th>
                <th scope="col">Satışa döndü</th>
                <th scope="col">Sipariş</th>
                <th scope="col">Ciro</th>
              </tr>
            </thead>
            <tbody>
              {view.sources.map((row) => (
                <tr key={row.source}>
                  <th scope="row">{row.label}</th>
                  <td className="mono">{row.conversations}</td>
                  <td className="mono">{row.replied}</td>
                  <td className="mono">
                    {row.converted}
                    {row.conversations > 0 ? (
                      <span className="customers-sources__pct"> · {formatPct(row.converted / row.conversations)}</span>
                    ) : null}
                  </td>
                  <td className="mono">{row.orders}</td>
                  <td className="mono">{formatUsd(centsToMicro(row.revenueCents), 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="musteri-liste" className="section">
        <SectionHeader id="musteri-liste" title="Müşteriler" count={filtered.length} />
        <input
          type="search"
          className="shop-search"
          aria-label="Müşteri ara"
          placeholder="ad, araç, kaynak ya da ülke"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {filtered.length === 0 ? (
          <p className="shop-hint">Aramaya uyan müşteri yok.</p>
        ) : (
          <ul className="customers-list" aria-label="Müşteriler">
            {filtered.map((customer) => (
              <CustomerRow
                key={customer.id}
                customer={customer}
                open={open === customer.id}
                onToggle={() => setOpen(open === customer.id ? null : customer.id)}
              />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
